import React, { useEffect } from "react";
import { Flex, ListItem, OrderedList, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickBasketOverseasAddressCard from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketOverseasAddressCard";
import PathPickStartGuideMenuPath from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideMenuPath";
import PathPickStartGuideExternalLink from "@/features/pathpick/features/start-guide/components/PathPickStartGuideExternalLink";
import PathPickStartGuideOfficialGuideLink from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideOfficialGuideLink";
import useExternalLinkGate from "@/features/pathpick/features/start-guide/hooks/useExternalLinkGate";
import { SMART_STORE_SELLER_INFO_URL } from "@/features/pathpick/features/start-guide/constants";
import { SMARTSTORE_OVERSEAS_RELEASE_GUIDE_URL } from "@/features/pathpick/features/basket/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const STEP_KEYS = ["step_1", "step_2", "step_3"] as const;

/**
 * 해외 출고지 등록 안내.
 *
 * 주소값과 칸별 복사 UI는 담은 상품 쪽 `PathPickBasketOverseasAddressCard` 를 그대로 쓴다 —
 * 주소가 두 곳에 각각 적히면 한쪽만 고쳐지는 사고가 난다.
 */
function PathPickStartGuideOverseasReleaseModal({
  isOpen,
  onClose,
  onComplete,
}: Props) {
  const { t } = useI18nHelper();
  const { hasOpened, markOpened, reset } = useExternalLinkGate();

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      minWidth={"720px"}
      isCentered={true}
      scrollBehavior={"inside"}
      maxHeight={"92vh"}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick_guide:overseas_release.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          <PathPickStartGuideMenuPath
            path={t("path_pick_guide:overseas_release.menu_path")}
          />
          {/* 절차는 순서가 의미를 갖는 목록이다 — 스크린리더에도 목록으로 읽힌다. */}
          <OrderedList
            aria-label={t("path_pick_guide:overseas_release.steps_aria_label")}
            spacing={"6px"}
            paddingLeft={"18px"}
          >
            {STEP_KEYS.map((key) => (
              <ListItem key={key}>
                <Text textStyle={"body-sm-medium"} color={"#444440"}>
                  {t(`path_pick_guide:overseas_release.${key}`)}
                </Text>
              </ListItem>
            ))}
          </OrderedList>
          {/* 옮겨 적을 주소는 절차 뒤에 둔다(데모와 같은 순서). */}
          <PathPickBasketOverseasAddressCard />
          <PathPickStartGuideOfficialGuideLink
            href={SMARTSTORE_OVERSEAS_RELEASE_GUIDE_URL}
          />
          <Flex
            gap={"8px"}
            flexWrap={"wrap"}
            justifyContent={"flex-end"}
            paddingTop={"4px"}
          >
            <PathPickStartGuideExternalLink
              href={SMART_STORE_SELLER_INFO_URL}
              label={t("path_pick_guide:overseas_release.open_cta")}
              newWindowLabel={t("path_pick_guide:external_link_suffix")}
              onOpen={markOpened}
              isPrimary={!hasOpened}
            />
            <AppButton
              variant={"primary"}
              isDisabled={!hasOpened}
              onClick={onComplete}
            >
              {t("path_pick_guide:overseas_release.done_cta")}
            </AppButton>
            <AppButton variant={"normal"} onClick={onClose}>
              {t("path_pick_guide:close")}
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

export default PathPickStartGuideOverseasReleaseModal;
